// ─────────────────────────────────────────────────────────────────────────────
// SEGMENTATION — MediaPipe ImageSegmenter (person mask for occlusion)
// Produces a foreground alpha mask used to draw arms OVER the garment overlay
// ─────────────────────────────────────────────────────────────────────────────
import {
  ImageSegmenter,
  FilesetResolver,
} from "@mediapipe/tasks-vision";

export let _segmenter: ImageSegmenter | null = null;
let _loading = false;
let _failed = false;

export async function getSegmenter(): Promise<ImageSegmenter | null> {
  if (_segmenter) return _segmenter;
  if (_failed) return null;
  if (_loading) {
    await new Promise<void>(res => {
      const iv = setInterval(() => {
        if (!_loading) { clearInterval(iv); res(); }
      }, 100);
    });
    return _segmenter;
  }

  _loading = true;
  try {
    const vision = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm"
    );
    _segmenter = await ImageSegmenter.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath:
          "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite",
        delegate: "GPU",
      },
      runningMode: "VIDEO",
      outputCategoryMask: false,
      outputConfidenceMasks: true,
    });
    console.log("[AR] ImageSegmenter ready ✓");
  } catch (err) {
    console.warn("[AR] ImageSegmenter failed — occlusion disabled", err);
    _failed = true;
  }
  _loading = false;
  return _segmenter;
}

export function closeSegmenter() {
  _segmenter?.close();
  _segmenter = null;
  _failed = false;
}

// ── Apply segmentation mask to an offscreen canvas ────────────────────────────
// Returns an ImageData where alpha = person confidence (for compositing arms)
// Returns a grayscale ImageData where R channel = person confidence.
// Used as an alphaMap on the occlusion mesh (Three.js reads R for alphaMap).
// High R = person present = draw video back over garment (arms in front).
// Low R  = background     = transparent = garment shows through.
export function maskToImageData(
  maskFloat32: Float32Array,
  width: number,
  height: number,
): ImageData {
  const data = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    const conf = Math.round(maskFloat32[i] * 255);
    data[i * 4 + 0] = conf; // R = confidence (alphaMap reads R channel)
    data[i * 4 + 1] = conf; // G
    data[i * 4 + 2] = conf; // B
    data[i * 4 + 3] = 255;  // A = always opaque so texture samples correctly
  }
  return new ImageData(data, width, height);
}
